// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// main.ts — boot, screens, and the wiring between the rulebook, the bot and the room.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
  type Lobby,
} from '@ben-gy/game-engine/lobby';

import { COURSES, KIND_GLYPHS, KIND_NAMES, MAX_PLAYERS, MIN_PLAYERS, SIDE, courseCap } from './kinds';
import { DEFAULT_MODE, MODE_IDS, modeOf, type ModeId } from './modes';
import { Game, type Standing } from './game';
import { SILL, SPOIL, isLegal, type Move } from './rules';
import { SKILLS, SKILL_IDS, Searcher, skillOf } from './search';
import { destinations } from './read';
import {
  boardHtml,
  columnStripHtml,
  esc,
  racksHtml,
  railHtml,
  setSeatNames,
  sheetHtml,
  sillHtml,
  tile,
  type Seat,
} from './view';
import { runCountdown } from './countdown';

const SLUG = 'kiln';
const app = document.getElementById('app')!;
const store = createStore(SLUG);

const sfx = createSfx({
  muted: !(store.get('sound', true) as boolean),
  patches: {
    tap: { type: 'square', freq: [420, 480], dur: 0.05, gain: 0.16 },
    take: { type: 'triangle', freq: [300, 420], dur: 0.1, gain: 0.2 },
    lay: { type: 'sine', freq: [520, 680], dur: 0.14, gain: 0.24 },
    run: { type: 'sine', freq: [660, 990], dur: 0.1, gain: 0.2 },
    spoilhit: { type: 'sawtooth', freq: [190, 90], dur: 0.14, gain: 0.18, noise: true },
    round: { type: 'triangle', freq: [300, 540], dur: 0.3, gain: 0.22 },
    win: { type: 'triangle', freq: [520, 1040], dur: 0.5, gain: 0.28 },
    lose: { type: 'sawtooth', freq: [400, 120], dur: 0.5, gain: 0.28 },
  },
});
const soundOn = (): boolean => !sfx.muted();

app.innerHTML =
  `<main class="main-content" id="content"></main>` +
  `<footer class="site-footer">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a></footer>`;
const content = document.getElementById('content')!;

// ---------------------------------------------------------------- session state

let game: Game | null = null;
let seats: Seat[] = [];
let selfSeat = 0;
let botSkill = skillOf(store.get('skill', 'journeyman'));
let modeChoice: ModeId = modeOf(store.get('mode', DEFAULT_MODE)).id;
let hostMode: ModeId = modeChoice;
let playerCount = Math.max(MIN_PLAYERS, Math.min(MAX_PLAYERS, Number(store.get('players', 2)) || 2));
let selected: { src: number; kind: number } | null = null;
let pendingCourse: number | null = null;
let viewSeat = 0;
let busy = false;
let mismatch = false;
let tally: number[] = [];

let net: Net | null = null;
let rounds: Rounds<{ mode: string }> | null = null;
let lobby: Lobby | null = null;
let roomCode = '';
let sendLog: ((m: { p: number[]; d: number }) => void) | null = null;
let logOff: (() => void) | null = null;
let deepLinkUsed = false;
let walkTimer: ReturnType<typeof setInterval> | null = null;
let walkLeft = 0;

const cleanups: (() => void)[] = [];
const onCleanup = (fn: () => void): void => void cleanups.push(fn);
function teardown(): void {
  while (cleanups.length) cleanups.pop()!();
  selected = null;
  pendingCourse = null;
  busy = false;
}
const setPlaying = (on: boolean): void => void document.body.classList.toggle('playing', on);
const isNet = (): boolean => net !== null && seats.length > 0 && !seats.some((s) => s.bot);

// ---------------------------------------------------------------- menu

function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = `
    <section class="screen menu">
      <h1 class="logo">Kiln</h1>
      <p class="tag">Take every tile of one kind — and whatever you leave behind is what everyone else gets.</p>
      <div class="menu-btns">
        <button class="btn primary" id="solo">Play</button>
        <button class="btn" id="friends">Play with friends</button>
        <button class="btn ghost" id="help">How to play</button>
        <button class="btn ghost" id="about">About</button>
      </div>
      <button class="btn ghost mute" id="mute" aria-pressed="${!soundOn()}">${soundOn() ? 'Sound on' : 'Sound off'}</button>
    </section>`;
  content.querySelector('#solo')!.addEventListener('click', () => {
    sfx.unlock();
    showSetup();
  });
  content.querySelector('#friends')!.addEventListener('click', () => {
    sfx.unlock();
    showRoomEntry();
  });
  content.querySelector('#help')!.addEventListener('click', () => showHelp());
  content.querySelector('#about')!.addEventListener('click', () => showAbout());
  const mute = content.querySelector<HTMLButtonElement>('#mute')!;
  mute.addEventListener('click', () => {
    sfx.setMuted(soundOn());
    store.set('sound', soundOn());
    mute.textContent = soundOn() ? 'Sound on' : 'Sound off';
    mute.setAttribute('aria-pressed', String(!soundOn()));
  });
  if (!store.get('seen', false)) {
    store.set('seen', true);
    showHelp();
  }
}

function showSetup(): void {
  teardown();
  setPlaying(false);
  const modeBtns = MODE_IDS.map(
    (id) =>
      `<button class="chip mode-btn${id === modeChoice ? ' on' : ''}" data-mode="${id}">${esc(modeOf(id).name)}</button>`,
  ).join('');
  const countBtns = [2, 3, 4]
    .map((n) => `<button class="chip count-btn${n === playerCount ? ' on' : ''}" data-count="${n}">${n}</button>`)
    .join('');
  const skillBtns = SKILL_IDS.map(
    (id) => `<button class="chip skill-btn${id === botSkill.id ? ' on' : ''}" data-skill="${id}">${esc(SKILLS[id].name)}</button>`,
  ).join('');
  content.innerHTML = `
    <section class="screen setup">
      <h2>Set the kiln</h2>
      <div class="opt"><h3>Mode</h3><div class="chips" id="modes">${modeBtns}</div>
        <p class="blurb" id="blurb">${esc(modeOf(modeChoice).blurb)}</p></div>
      <div class="opt"><h3>Players</h3><div class="chips" id="counts">${countBtns}</div>
        <p class="blurb">You, and the rest of the bench.</p></div>
      <div class="opt"><h3>Opponents</h3><div class="chips" id="skills">${skillBtns}</div></div>
      <div class="menu-btns">
        <button class="btn primary" id="start">Fire it up</button>
        <button class="btn ghost" id="back">Back</button>
      </div>
    </section>`;
  const pick = (sel: string, attr: string, fn: (v: string) => void): void => {
    content.querySelectorAll<HTMLElement>(sel).forEach((b) =>
      b.addEventListener('click', () => {
        fn(b.dataset[attr]!);
        showSetup();
      }),
    );
  };
  pick('.mode-btn', 'mode', (v) => {
    modeChoice = modeOf(v).id;
    store.set('mode', modeChoice);
  });
  pick('.count-btn', 'count', (v) => {
    playerCount = Math.max(MIN_PLAYERS, Math.min(MAX_PLAYERS, Number(v)));
    store.set('players', playerCount);
  });
  pick('.skill-btn', 'skill', (v) => {
    botSkill = skillOf(v);
    store.set('skill', botSkill.id);
  });
  content.querySelector('#start')!.addEventListener('click', () => startSolo());
  content.querySelector('#back')!.addEventListener('click', showMenu);
}

// ---------------------------------------------------------------- overlays

function overlay(title: string, body: string): void {
  const el = document.createElement('div');
  el.className = 'modal';
  el.innerHTML = `<div class="modal-card" role="dialog" aria-modal="true" aria-label="${esc(title)}">
      <button class="modal-x" aria-label="Close">✕</button>
      <h2>${esc(title)}</h2>${body}</div>`;
  document.body.appendChild(el);
  const close = (): void => {
    el.remove();
    document.removeEventListener('keydown', key);
  };
  const key = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  el.querySelector('.modal-x')!.addEventListener('click', close);
  // A modal opened from a pointerup is closed by that same tap's trailing click, so the backdrop
  // dismiss is armed late. All four exits are required: the ✕, outside, Escape, and a real backdrop.
  const armAt = Date.now() + 350;
  el.addEventListener('pointerdown', (e) => {
    if (Date.now() < armAt) return;
    if (!(e.target as HTMLElement).closest('.modal-card')) close();
  });
  document.addEventListener('keydown', key);
  onCleanup(close);
}

function showHelp(): void {
  overlay(
    'How to play',
    `<p>Take <b>every tile of one kind</b> from a rack. Everything you leave behind tumbles into the
     <b>sill</b> — where anyone can take it.</p>
     <p>Your tiles go into <b>one course</b>. A course must be empty or already hold that kind, and
     anything that will not fit falls onto your <b>spoil</b> and costs you points.</p>
     <p>When the table is bare, every <b>full</b> course lays one tile into your mosaic — worth the
     length of the runs it joins.</p>
     <p>The game ends after the round somebody completes a <b>row</b>. Then +2 a row, +7 a column,
     and +10 for any kind you laid all five times.</p>
     <p class="kinds">${KIND_NAMES.map((n, i) => `${tile(i)} ${esc(n)}`).join(' ')}</p>`,
  );
}

function showAbout(): void {
  overlay(
    'About',
    `<p>Kiln is a tile-drafting game about what you leave behind. Built by
       <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>
     <p>No accounts, no cookies, no tracking. Page views are counted anonymously and without cookies
       by Cloudflare Web Analytics, and that is the only analytics here.</p>
     <p>Playing with friends connects your browsers <b>directly to each other</b> over WebRTC. A
       public signalling relay is used only to introduce them, and no game state ever reaches a
       server. Because the connection is direct, the people in your room can see your device's
       <b>IP addresses</b> — which is why a room is invite-only and there is no public list of games
       to drop into.</p>
     <p>Colours are backed up by a shape on every tile, so the game reads without them.</p>`,
  );
}

// ---------------------------------------------------------------- solo

function startSolo(): void {
  teardown();
  const seed = (Math.random() * 0xffffffff) >>> 0;
  seats = [{ name: 'You', bot: false }];
  for (let i = 1; i < playerCount; i++) seats.push({ name: botName(i), bot: true });
  selfSeat = 0;
  viewSeat = 0;
  tally = new Array(playerCount).fill(0);
  game = new Game(modeChoice, playerCount, seed);
  setSeatNames(seats.map((s) => s.name));
  showGame();
  paint();
  pump();
}

const BOT_NAMES = ['Kestrel', 'Vane', 'Rook', 'Tamsin', 'Orrin', 'Peel'];
const botName = (i: number): string => BOT_NAMES[(i - 1) % BOT_NAMES.length];

/** Drive every bot seat that is due, off setTimeout — never rAF, which a hidden tab never fires. */
function pump(): void {
  if (!game || game.over || busy) return;
  if (isNet() || !seats[game.seat]?.bot) return;
  busy = true;
  paintTurn();
  setTimeout(() => {
    busy = false;
    if (!game || game.over) return;
    const r = new Searcher().choose(game.table, botSkill, Math.random);
    if (!r.move || !isLegal(game.table, r.move)) return void finish();
    commit(r.move, false);
  }, 260);
}

// ---------------------------------------------------------------- the play screen

function showGame(): void {
  teardown();
  setPlaying(true);
  content.innerHTML = `
    <section class="screen play" id="play">
      <header class="topbar">
        <span class="round" id="round"></span>
        <span class="turn" id="turn"></span>
        <button class="icon" id="rules" aria-label="How to play">?</button>
        <button class="icon" id="menu" aria-label="Menu">≡</button>
      </header>
      <section class="racks" id="racks" aria-label="Racks"></section>
      <section class="sill" id="sill" aria-label="The sill"></section>
      <section class="myboard" id="myboard"></section>
      <section class="rail" id="rail" aria-label="The other benches"></section>
    </section>
    <div class="sheet" id="sheet" hidden></div>`;
  content.querySelector('#rules')!.addEventListener('click', () => showHelp());
  content.querySelector('#menu')!.addEventListener('click', () => showQuit());
  content.querySelector('#racks')!.addEventListener('click', onPick);
  content.querySelector('#sill')!.addEventListener('click', onPick);
  content.querySelector('#rail')!.addEventListener('click', onRail);
  const sheet = content.querySelector<HTMLElement>('#sheet')!;
  sheet.addEventListener('click', onSheet);
  const key = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeSheet();
  };
  document.addEventListener('keydown', key);
  onCleanup(() => document.removeEventListener('keydown', key));
  applyMetrics();
}

function applyMetrics(): void {
  const el = content.querySelector<HTMLElement>('#play');
  if (!el || !game) return;
  el.style.setProperty('--rack-rows', String(Math.ceil(game.table.rackCount / 2)));
  el.style.setProperty('--players', String(game.players));
}

function paint(): void {
  if (!game) return;
  const t = game.table;
  const q = (id: string): HTMLElement | null => content.querySelector<HTMLElement>(id);
  const racks = q('#racks');
  if (!racks) return;
  racks.innerHTML = racksHtml(t, selected);
  q('#sill')!.innerHTML = sillHtml(t, selected);
  q('#myboard')!.innerHTML = boardHtml(t, viewSeat, seats, viewSeat === selfSeat);
  q('#rail')!.innerHTML = railHtml(t, viewSeat, seats);
  paintTurn();
}

function paintTurn(): void {
  if (!game) return;
  const r = content.querySelector<HTMLElement>('#round');
  const tn = content.querySelector<HTMLElement>('#turn');
  if (!r || !tn) return;
  r.textContent = `Round ${game.round}`;
  if (mismatch) {
    tn.textContent = 'Different build — reload';
    tn.className = 'turn warn';
    return;
  }
  if (walkTimer) {
    tn.textContent = `Alone — finishing in ${Math.ceil(walkLeft / 1000)}s`;
    tn.className = 'turn warn';
    return;
  }
  const mine = game.seat === selfSeat && !game.over;
  tn.textContent = game.over ? 'Done' : mine ? 'Your turn' : `${seats[game.seat]?.name ?? ''}…`;
  tn.className = `turn${mine ? ' mine' : ''}`;
}

function onPick(e: Event): void {
  const b = (e.target as HTMLElement).closest<HTMLElement>('[data-src]');
  if (!b || !game || game.over) return;
  if (game.seat !== selfSeat || busy) return;
  const src = Number(b.dataset.src);
  const kind = Number(b.dataset.kind);
  selected = { src, kind };
  pendingCourse = null;
  sfx.play('tap');
  paint();
  openSheet();
}

function onRail(e: Event): void {
  const b = (e.target as HTMLElement).closest<HTMLElement>('[data-seat]');
  if (!b || !game) return;
  const s = Number(b.dataset.seat);
  viewSeat = viewSeat === s ? selfSeat : s;
  sfx.play('blip');
  paint();
}

function openSheet(): void {
  if (!game || !selected) return;
  const sheet = content.querySelector<HTMLElement>('#sheet')!;
  sheet.innerHTML =
    pendingCourse === null
      ? sheetHtml(game.table, selfSeat, selected.src, selected.kind)
      : columnStripHtml(game.table, selfSeat, pendingCourse, selected.kind);
  sheet.hidden = false;
  const armAt = Date.now() + 350;
  sheet.dataset.arm = String(armAt);
}

function closeSheet(): void {
  const sheet = content.querySelector<HTMLElement>('#sheet');
  if (!sheet) return;
  sheet.hidden = true;
  sheet.innerHTML = '';
  selected = null;
  pendingCourse = null;
  paint();
}

function onSheet(e: Event): void {
  const el = e.target as HTMLElement;
  const sheet = content.querySelector<HTMLElement>('#sheet')!;
  if (el.closest('[data-cancel]')) {
    if (pendingCourse !== null) {
      pendingCourse = null;
      return openSheet();
    }
    return closeSheet();
  }
  if (!el.closest('.sheet-inner')) {
    if (Date.now() < Number(sheet.dataset.arm ?? 0)) return;
    return closeSheet();
  }
  if (!game || !selected) return;

  const col = el.closest<HTMLElement>('[data-col]');
  if (col && pendingCourse !== null) {
    return void commitFromSheet(pendingCourse, Number(col.dataset.col));
  }
  const row = el.closest<HTMLElement>('[data-dest]');
  if (!row) return;
  const dest = Number(row.dataset.dest);
  if (dest !== SPOIL && !game.table.mode.printed) {
    const i = selfSeat * COURSES + dest;
    if (game.table.courseCol[i] === -1) {
      pendingCourse = dest;
      sfx.play('tap');
      return openSheet();
    }
  }
  commitFromSheet(dest, -1);
}

function commitFromSheet(dest: number, col: number): void {
  if (!game || !selected) return;
  const m: Move = { src: selected.src, kind: selected.kind, dest, col: dest === SPOIL ? -1 : col };
  if (!isLegal(game.table, m)) return;
  closeSheet();
  commit(m, true);
}

function commit(m: Move, mine: boolean): void {
  if (!game) return;
  const res = game.play(m);
  if (!res) return;
  sfx.play(m.dest === SPOIL ? 'spoilhit' : 'take');
  for (const l of res.lays) {
    sfx.play('lay', { pitch: 1 + l.gain / 20 });
    if (l.gain > 2) sfx.play('run', { pitch: 1 + l.gain / 12 });
  }
  if (res.roundEnded && !game.over) sfx.play('round');
  if (mine || !isNet()) paint();
  else paint();
  if (isNet()) broadcast();
  if (game.over) return void finish();
  pump();
}

function showQuit(): void {
  overlay(
    'Menu',
    `<div class="menu-btns">
      <button class="btn" data-act="resume">Keep playing</button>
      ${isNet() ? '<button class="btn ghost" data-act="lobby">Back to lobby</button>' : ''}
      <button class="btn ghost" data-act="menu">Leave the game</button>
    </div>`,
  );
  document.querySelectorAll<HTMLElement>('.modal [data-act]').forEach((b) =>
    b.addEventListener('click', () => {
      const act = b.dataset.act;
      document.querySelector('.modal')?.remove();
      if (act === 'lobby') backToLobby();
      else if (act === 'menu') void leaveAll();
    }),
  );
}

async function leaveAll(): Promise<void> {
  if (net) return void (await leaveRoom());
  game = null;
  showMenu();
}

// ---------------------------------------------------------------- results

function finish(): void {
  if (!game) return;
  stopWalkover();
  // Close the ROUND before painting results. The engine's lobby deliberately renders nothing while
  // the round's phase is still "playing" and you are seated — so without this the rematch controls
  // are simply absent from the results screen and "Play again" votes into a void. It looks like a
  // dead button and it is really a phase that never advanced.
  if (isNet()) rounds?.finish?.();
  const order = game.ranking();
  if (tally.length === game.players) tally[order[0]]++;
  sfx.play(order[0] === selfSeat ? 'win' : 'lose');
  showResults();
}

function showResults(): void {
  if (!game) return;
  teardown();
  setPlaying(false);
  const g = game;
  const order = g.ranking();
  const st = g.standings();
  const winner = order[0];
  const rows = order
    .map((s, i) => resultRow(st[s], s, i, winner))
    .join('');
  const head =
    winner === selfSeat
      ? 'You took it.'
      : `${esc(seats[winner]?.name ?? 'They')} took it.`;
  content.innerHTML = `
    <section class="screen results">
      <h2>${head}</h2>
      <p class="sub">${esc(summary(g, st, order))}</p>
      <div class="rtable">${rows}</div>
      <div class="menu-btns">
        ${isNet() ? '<button class="btn primary" id="again">Play again</button><button class="btn ghost" id="lobby">Back to lobby</button>' : '<button class="btn primary" id="again">Play again</button>'}
        <button class="btn ghost" id="menu">Menu</button>
        <button class="btn ghost results-feedback" id="fb">Tell us what you think</button>
      </div>
      <div id="rematch-host"></div>
    </section>`;
  content.querySelector('#menu')!.addEventListener('click', () => void leaveAll());
  content.querySelector('#fb')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#lobby')?.addEventListener('click', () => backToLobby());
  content.querySelector('#again')!.addEventListener('click', () => {
    if (isNet()) rounds?.vote();
    else startSolo();
  });
  // The lobby goes in its OWN slot, never into the results section itself: createLobby owns its
  // container's innerHTML, so handing it the whole screen wipes the very breakdown the screen exists
  // to show — everyone's outcome, which is the one thing a results screen must not lose.
  if (isNet()) showLobby(content.querySelector<HTMLElement>('#rematch-host') ?? undefined);
}

/** Everyone's outcome, always — and what each of them actually did to get it (principle #9). */
function resultRow(s: Standing, seat: number, place: number, winner: number): string {
  const bits: string[] = [];
  if (s.rows) bits.push(`${s.rows} row${s.rows > 1 ? 's' : ''}`);
  if (s.cols) bits.push(`${s.cols} column${s.cols > 1 ? 's' : ''}`);
  if (s.kinds) bits.push(`${s.kinds} full kind${s.kinds > 1 ? 's' : ''}`);
  const stars = tally[seat] ? `<span class="stars">${'★'.repeat(Math.min(tally[seat], 5))}</span>` : '';
  return `<div class="rrow${seat === winner ? ' win' : ''}${seat === selfSeat ? ' me' : ''}">
    <span class="rplace">${place + 1}</span>
    <span class="rname">${esc(seats[seat]?.name ?? `Seat ${seat + 1}`)}${stars}</span>
    <span class="rscore">${s.score}</span>
    <span class="rdetail">${s.laid} laid${bits.length ? ` · ${bits.join(' · ')}` : ''} · ${s.bonus > 0 ? `+${s.bonus} bonus · ` : ''}${s.spoiled} spoiled${s.stranded ? ` · ${s.stranded} stranded` : ''}</span>
  </div>`;
}

function summary(g: Game, st: Standing[], order: number[]): string {
  if (g.table.endReason === 'drained') return 'The clay ran out.';
  const win = st[order[0]];
  const next = st[order[1]];
  const gap = win.score - next.score;
  const stranded = st.reduce((a, s) => a + s.stranded, 0);
  const near = gap <= 3 ? `Only ${gap} in it.` : `${gap} clear.`;
  return `${near} ${g.round} rounds, and ${stranded} tile${stranded === 1 ? '' : 's'} left on the benches when it ended.`;
}

// ---------------------------------------------------------------- multiplayer

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  const deep = deepLinkUsed ? null : new URL(location.href).searchParams.get('room');
  if (deep) {
    deepLinkUsed = true;
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) return void enterRoom(code, false);
  }
  const entry = createRoomEntry({
    container: content,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Two to four benches, one kiln. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  game = null;
  seats = [];
  tally = [];
  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: () => repaintLobby(),
      onPeers: () => {
        // peers() IS the roster and it includes you. A room that has collapsed to one player
        // mid-round is a walkover with a visible horizon, never a wait with no end.
        const alone = n.peers().length <= 1;
        if (alone && game && !game.over && seats.length > 1) armWalkover();
        else if (!alone) stopWalkover();
        repaintLobby();
      },
      onPeerLeave: () => {
        if (game && !game.over && seats.length > 1) armWalkover();
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName: handle(),
    minPlayers: MIN_PLAYERS,
    roundOpts: () => ({ mode: modeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const send = n.channel<{ p: number[]; d: number }>('log', (data) => onRemoteLog(data));
  sendLog = (m) => send(m);
  // NOT onCleanup(): the cleanup list is per SCREEN and teardown() runs on every screen change,
  // including the one that starts a round. The channel belongs to the ROOM and is torn down only in
  // leaveRoom.
  logOff = () => send.off();

  showLobby();
}

function handle(): string {
  let h = store.get('handle', '') as string;
  if (!h) {
    h = `Bench ${100 + Math.floor(Math.random() * 900)}`;
    store.set('handle', h);
  }
  return h;
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  mismatch = false;
  const mode = modeOf(info.opts?.mode);
  const roster = info.players.slice(0, MAX_PLAYERS);
  seats = roster.map((p) => ({ name: p.name, bot: false }));
  selfSeat = roster.findIndex((p) => p.id === net?.selfId);
  if (!info.seated || selfSeat < 0) selfSeat = -1;
  viewSeat = Math.max(0, selfSeat);
  if (tally.length !== seats.length) tally = new Array(seats.length).fill(0);
  setSeatNames(seats.map((s) => s.name));
  game = new Game(mode, Math.max(MIN_PLAYERS, seats.length), info.seed);
  showGame();
  paint();
  runCountdown(content.querySelector<HTMLElement>('.play') ?? content, sfx, () => paint());
}

function broadcast(): void {
  // The WHOLE log, every time. A few dozen small integers, and it makes a dropped message a
  // non-event and a late peer instantly in sync.
  if (game && sendLog) sendLog({ p: game.plies.slice(), d: game.digest() });
}

function onRemoteLog(data: { p: number[]; d: number }): void {
  const g = game;
  if (!g || !Array.isArray(data?.p)) return;
  // The digest first, because replaying a log is only a probabilistic mismatch detector: a short log
  // of moves that are legal under both rulebooks replays perfectly, and the two builds then diverge
  // in silence a few plies later.
  if (typeof data.d === 'number' && data.d !== g.digest()) {
    mismatch = true;
    paintTurn();
    return;
  }
  const theirs = data.p.filter((x) => Number.isInteger(x) && x >= 0);
  if (theirs.length <= g.plies.length) return;
  // Replay their log through OUR generator. If it does not apply, they are running different code —
  // say so rather than diverging quietly.
  const rebuilt = Game.replay(g.mode, g.players, g.seed, theirs);
  if (!rebuilt) {
    mismatch = true;
    paintTurn();
    return;
  }
  game = rebuilt;
  selected = null;
  pendingCourse = null;
  sfx.play('blip');
  paint();
  if (rebuilt.over) finish();
}

/** Twelve seconds, visible, then the survivor takes it. A wait with no horizon is a hang. */
function armWalkover(): void {
  if (walkTimer || !game || game.over || selfSeat < 0) return;
  walkLeft = 12_000;
  walkTimer = setInterval(() => {
    walkLeft -= 500;
    if (walkLeft > 0) return void paintTurn();
    stopWalkover();
    if (game && !game.over) {
      game.walkover();
      finish();
    }
  }, 500);
  paintTurn();
}

function stopWalkover(): void {
  if (walkTimer) clearInterval(walkTimer);
  walkTimer = null;
  walkLeft = 0;
}

const repaintLobby = (): void => lobby?.repaint();

function backToLobby(): void {
  teardown();
  setPlaying(false);
  // The engine's lobby paints nothing while a round is still "playing" and we are seated, so a
  // mid-round exit has to finish the round first or the screen is simply blank.
  rounds?.finish?.();
  game = null;
  showLobby();
}

function showLobby(host?: HTMLElement): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return showMenu();
  setPlaying(false);
  if (!host) content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const container = host ?? content.querySelector<HTMLElement>('#lobby-host')!;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    onCancel: () => void leaveRoom(),
    modeSlot: () =>
      isHost()
        ? `<div class="lobby-modes" id="lm">${MODE_IDS.map(
            (id) =>
              `<button class="chip mode-btn${id === modeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeChoice}">${esc(modeOf(id).name)}</button>`,
          ).join('')}<p class="mode-blurb">${esc(modeOf(modeChoice).blurb)}</p></div>`
        : `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${esc(modeOf(hostMode).name)}</strong>` : 'Waiting for the host…'}</p>`,
    onModeMount: () => {
      if (!isHost()) return;
      container.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          modeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', modeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

async function leaveRoom(): Promise<void> {
  teardown();
  clearRoomInUrl();
  stopWalkover();
  const n = net;
  net = null;
  rounds = null;
  logOff?.();
  logOff = null;
  sendLog = null;
  seats = [];
  game = null;
  // Await the real teardown: Trystero defers it, and coming back to a room that is still dying is
  // the leave/rejoin trap that leaves every peer alone in the right room code, for good.
  if (n) await n.leave();
  showMenu();
}

// ---------------------------------------------------------------- boot

hardenViewport();
window.addEventListener('beforeunload', () => void net?.leave());
showMenu();

// A synchronous hook so the browser pass can drive a whole game to its end without depending on rAF,
// which never fires in a background tab.
(window as unknown as { __kiln: unknown }).__kiln = {
  state: () => game,
  table: () => game?.table,
  seats: () => seats,
  selfSeat: () => selfSeat,
  metrics: () => ({ mode: game?.mode.id, players: game?.players, round: game?.round }),
  dests: () =>
    game && selected ? destinations(game.table, selfSeat, selected.src, selected.kind) : null,
  select: (src: number, kind: number) => {
    if (!game) return false;
    selected = { src, kind };
    pendingCourse = null;
    paint();
    openSheet();
    return true;
  },
  play: (m: Move) => {
    if (!game || !isLegal(game.table, m)) return false;
    commit(m, true);
    return true;
  },
  step: () => {
    if (!game || game.over) return false;
    const r = new Searcher().choose(game.table, { ...SKILLS.journeyman, nodes: 900, timeMs: 0 });
    if (!r.move) return false;
    commit(r.move, true);
    return true;
  },
  glyphs: KIND_GLYPHS,
  sides: SIDE,
  caps: Array.from({ length: COURSES }, (_, i) => courseCap(i)),
  sill: SILL,
  spoil: SPOIL,
};
